<template>
  <div class="base-extraction" :class="[method, { 'is-selected-annotation': isSelectedAnnotation }]">
    <div class="__text-container" :class="{ 'is-clamped': isClamped }">
      <div class="__text" ref="text" :class="{ compact: isCompact }" @click="isCompact && extractionClicked()">
        {{ text }}
        <BaseInlineTextSource
          v-if="showIcon"
          :method="method"
          :documentId="source"
          compact
          @click.native="extractionClicked"
        />
      </div>
    </div>
    <BaseRoundButton
      v-if="actionable"
      class="base-extraction__icon"
      :size="20"
      is-transparent
      icon="copy"
      :tooltip-text="$t('actions.copy')"
      tooltipPlacement="top"
      @click.native="onCopyClicked"
    />
    <BaseRoundButton
      v-if="actionable"
      class="base-extraction__icon"
      :size="20"
      is-transparent
      is-red
      icon="trash-2"
      :tooltip-text="$t('actions.delete')"
      tooltipPlacement="top"
      @click.native="onDeleteClicked"
    />
  </div>
</template>

<script>
import BaseInlineTextSource from '@/components/base/BaseInlineTextSource.vue';
import BaseRoundButton from '@/components/base/BaseRoundButton.vue';
import { mapGetters } from 'vuex';

export default {
  name: 'BaseExtraction',
  components: {
    BaseRoundButton,
    BaseInlineTextSource,
  },
  props: {
    evalGridValue: {
      type: Object,
      default: () => null,
    },
    actionable: {
      type: Boolean,
      default: false,
    },
    text: {
      type: String,
      required: true,
    },
    method: {
      type: String,
      required: false,
      default: '',
    },
    source: {
      type: String,
      required: false,
      default: '',
    },
    isCompact: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      isClamped: false,
    };
  },
  computed: {
    ...mapGetters('annotations', ['selectedAnnotation']),
    showIcon() {
      return this.method !== 'NO_SOURCE';
    },
    isSelectedAnnotation() {
      if (!this.evalGridValue || !this.evalGridValue.textAnnotationId) {
        return false;
      }
      return this.selectedAnnotation && this.selectedAnnotation.id === this.evalGridValue.textAnnotationId;
    },
  },
  watch: {
    text() {
      this.$nextTick(this.checkClamped);
    },
  },
  methods: {
    extractionClicked() {
      this.$emit('extraction-source-clicked', this.evalGridValue);
    },
    onDeleteClicked() {
      this.$emit('delete', this.evalGridValue);
    },
    checkClamped() {
      const el = this.$refs.text;
      if (el && this.isCompact) {
        this.isClamped = el.scrollHeight > el.clientHeight + 1;
      }
    },
    onCopyClicked() {
      navigator.clipboard
        .writeText(this.text)
        .then(() => this.$toast.success('Successfully copied text to clipboard!'))
        .catch(() => this.$toast.error('Failed to copy text to clipboard!'));
    },
  },
  mounted() {
    this.$nextTick(this.checkClamped);
  },
};
</script>

<style lang="scss">
.base-extraction {
  padding: 4px 4px 0px 4px;
  display: flex;
  align-items: start;
  justify-content: space-between;
  width: 100%;
  gap: 8px;
  border-radius: $border-radius-medium;

  &:hover {
    background: $color-hover;
  }

  &.is-selected-annotation {
    background-color: $color-accent-200;
  }

  .__text-container {
    flex: 1;
    min-width: 0;

    .__text {
      color: $color-normal;
      line-height: 1.5;
      font-size: 14px;
      width: auto;

      &.compact {
        display: -webkit-box;
        line-clamp: 3;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 3;
        overflow: hidden;
        cursor: pointer;
      }
    }

    &.is-clamped {
      position: relative;
      padding-bottom: 24px; // make room for the icon

      .base-inline-text-source {
        position: absolute;
        bottom: 0;
        left: 0;
      }
    }
  }

  &__icon {
    flex-shrink: 0;
    opacity: 0;
    transition: opacity 0.15s ease;

    @include no-hover {
      opacity: 1;
    }

    .base-icon {
      width: 16px;
      height: 16px;
    }
  }

  &:hover &__icon {
    opacity: 1;
  }
}
</style>
